import { useState, useEffect } from 'react';
import { PRIMARY_COLOR } from '../../constants/color';
import { Box, Grid } from '@mui/material';
import { SECTION_BORDER_PIXEL } from '../../constants/section';
import { cloudfrontUrl } from '../../constants/imageUrl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Category, Product } from '../../types/product';
import { products, categories } from '../../data/products';
import useIsMobile from '../../hooks/useIsMobile';
import { updatePageSEO, seoConfigs } from '../../utils/seo';

export default function Products() {
  // Navigation
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile('md');

  // State
  const [selectedCategory, setSelectedCategory] = useState<Category>(
    Category.SIGATURE_PLATTER,
  );

  // Update SEO for products page
  useEffect(() => {
    updatePageSEO(seoConfigs.products);
  }, []);

  // Handle category from URL params, history state, or hash when navigating back from product detail
  useEffect(() => {
    // First check URL search params (for direct navigation back)
    const categoryFromParams = searchParams.get('category');
    if (
      categoryFromParams &&
      Object.values(Category).includes(categoryFromParams as Category)
    ) {
      setSelectedCategory(categoryFromParams as Category);
      return;
    }

    // Then check browser history state (for browser back button)
    const historyState = window.history.state;
    if (
      historyState?.category &&
      Object.values(Category).includes(historyState.category as Category)
    ) {
      setSelectedCategory(historyState.category as Category);
      return;
    }

    // Finally check URL hash (for our custom back navigation)
    const hash = window.location.hash.substring(1); // Remove the # symbol
    if (hash && Object.values(Category).includes(hash as Category)) {
      setSelectedCategory(hash as Category);
      // Clear the hash from URL after setting the category
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [searchParams]);

  const filteredProducts = products
    .filter((product) => product.category === selectedCategory)
    .filter((product) => product.isVisible);

  // Calculate grid size based on number of products
  const getGridSize = (count: number) => {
    if (count === 1) return { xs: 6, md: 6 }; // Mobile: half width, Desktop: full width
    if (count === 2 || count === 3) return { xs: 6, md: 4 }; // Mobile: half width each, Desktop: third width each
    if (count === 4) return { xs: 6, md: 3 }; // Mobile: half width each, Desktop: quarter width
    return { xs: 6, md: 3 }; // Mobile: half width each, Desktop: quarter width
  };

  // Calculate aspect ratio to maintain consistent height
  const getAspectRatio = (count: number) => {
    // On mobile (xs), always use 1/1.5 since products are full width
    // On desktop (md+), adjust based on count
    if (count === 1) return { xs: '1/1.5', md: '1.33/1.5' }; // Mobile: normal, Desktop: 4x wider
    if (count === 2 || count === 3) return { xs: '1/1.5', md: '1.33/1.5' }; // Mobile: normal, Desktop: 1.33x wider
    return { xs: '1/1.5', md: '1/1.5' }; // Default aspect ratio for both
  };

  const gridSize = getGridSize(filteredProducts.length);

  const categoryButtonImageMap = {
    [Category.BURGER]: `${cloudfrontUrl}/food/yangnyeom_chicken_burger.png`,
    [Category.CORNDOG]: `${cloudfrontUrl}/food/corndog.png`,
    [Category.CHICKEN]: `${cloudfrontUrl}/food/chicken2.png`,
    [Category.SIGATURE_PLATTER]: `${cloudfrontUrl}/food/truffle_burger.png`,
    [Category.SIDE]: `${cloudfrontUrl}/food/Tteokbokki.png`,
    [Category.COCKTAIL]: `${cloudfrontUrl}/food/jujube.png`,
    [Category.MATCHA_SMOOTHIE]: `${cloudfrontUrl}/food/녹차딸기라떼(Matcha Fraise Latte).png`,
    [Category.DESSERT]: `${cloudfrontUrl}/food/ksmweb_glace_matcha.png`,
  };

  const categoryTextMap = {
    [Category.SIGATURE_PLATTER]: (
      <div
        className="flex flex-col justify-between"
        style={{ minHeight: '100%' }}
      >
        <div style={{ marginBottom: '0.5rem' }}>
          <h1
            style={{
              fontSize: isMobile
                ? 'clamp(0.9rem, 3.5vw, 1.3rem)'
                : 'clamp(3rem, 8vw, 12rem)',
              lineHeight: isMobile ? '1.1' : '100%',
              margin: 0,
            }}
          >
            MENU TRUFFLE CHICKEN BURGER
          </h1>
        </div>
        <div>
          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.6rem, 2.2vw, 0.9rem)' : '2rem',
              margin: '0.1rem 0',
            }}
          >
            Édition Hiver
          </h1>

          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.7rem, 2.5vw, 1rem)' : '3rem',
              margin: '0.1rem 0',
            }}
          >
            Burger au Poulet à la Truffe
          </h1>
          <h4
            style={{
              fontSize: isMobile ? 'clamp(0.6rem, 2.2vw, 0.9rem)' : '1rem',
              margin: '0.3rem 0',
            }}
          >
            Un burger d’hiver en édition limitée, élaboré avec une sauce à la
            truffe développée par un chef étoilé Michelin. Créé spécialement
            pour la saison de Noël, ce burger associe sauce à la truffe, fromage
            à raclette fondant et onion frits croustillants, pour une saveur
            riche et réconfortante.
          </h4>

          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.7rem, 2.5vw, 1rem)' : '3rem',
              margin: '0.1rem 0',
            }}
          >
            Pommes Noisette
          </h1>
          <h4
            style={{
              fontSize: isMobile ? 'clamp(0.6rem, 2.2vw, 0.9rem)' : '1rem',
              margin: '0.3rem 0',
            }}
          >
            En accompagnement du menu, des pommes noisette croustillantes,
            servies avec notre sauce à la truffe maison, pour prolonger
            l’expérience gourmande du burger.
          </h4>
        </div>
      </div>
    ),
    [Category.BURGER]: (
      <div
        className="flex flex-col justify-between h-full"
        style={{ overflow: 'hidden' }}
      >
        <div style={{ flexShrink: 0 }}>
          <h1
            style={{
              fontSize: isMobile
                ? 'clamp(1rem, 4vw, 1.5rem)'
                : 'clamp(3rem, 8vw, 12rem)',
              lineHeight: isMobile ? '1.1' : '100%',
              margin: 0,
            }}
          >
            BURGER
          </h1>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            paddingTop: isMobile ? '1rem' : '0',
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.9rem, 3.2vw, 1.3rem)' : '2rem',
              margin: '0.5rem 0',
            }}
          >
            Burgers signature, entre pain artisanal et poulet mariné
            croustillant à cœur, sublimés sauces originales, toutes élaborées
            maison.
          </h1>
        </div>
      </div>
    ),
    [Category.CORNDOG]: (
      <div
        className="flex flex-col justify-between h-full"
        style={{ overflow: 'hidden' }}
      >
        <div style={{ flexShrink: 0 }}>
          <h1
            style={{
              fontSize: isMobile
                ? 'clamp(1rem, 4vw, 1.5rem)'
                : 'clamp(3rem, 8vw, 12rem)',
              lineHeight: isMobile ? '1.1' : '100%',
              margin: 0,
            }}
          >
            CORNDOG
          </h1>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            paddingTop: isMobile ? '1rem' : '0',
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.9rem, 3.2vw, 1.3rem)' : '2rem',
              margin: '0.5rem 0',
            }}
          >
            Corndogs maison, symbole de la street-food coréenne : saucisse de
            poulet enrobée d'une pâte artisanale, frits minute pour un
            croustillant doré et gourmand.
          </h1>
        </div>
      </div>
    ),
    [Category.CHICKEN]: (
      <div
        className="flex flex-col justify-between h-full"
        style={{ overflow: 'hidden' }}
      >
        <div style={{ flexShrink: 0 }}>
          <h1
            style={{
              fontSize: isMobile
                ? 'clamp(1rem, 4vw, 1.5rem)'
                : 'clamp(3rem, 8vw, 12rem)',
              lineHeight: isMobile ? '1.1' : '100%',
              margin: 0,
            }}
          >
            GOLDEN CHICKEN
          </h1>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            paddingTop: isMobile ? '1rem' : '0',
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.9rem, 3.2vw, 1.3rem)' : '2rem',
              margin: '0.5rem 0',
            }}
          >
            Trois pièces de cuisse poulet fermier et desossés, marinés avec soin
            et enrobés d'une panure maison, frits minute pour un croustillant
            doré et une saveur unique.
          </h1>
        </div>
      </div>
    ),
    [Category.SIDE]: (
      <div
        className="flex flex-col justify-between h-full"
        style={{ overflow: 'hidden' }}
      >
        <div style={{ flexShrink: 0 }}>
          <h1
            style={{
              fontSize: isMobile ? 'clamp(1rem, 4vw, 1.5rem)' : '12rem',
              lineHeight: isMobile ? '1.1' : '100%',
              margin: 0,
            }}
          >
            PETIT FAIM
          </h1>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            paddingTop: isMobile ? '1rem' : '0',
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.9rem, 3.2vw, 1.3rem)' : '2rem',
              margin: '0.5rem 0',
            }}
          >
            Entrées revisitées, inspirées des saveurs coréennes et pensées pour
            les petites faims :
          </h1>
          <h3
            style={{
              fontSize: isMobile ? 'clamp(0.6rem, 2vw, 0.8rem)' : '1rem',
              margin: '0.2rem 0',
            }}
          >
            des bouchees creatives et gourmandes a déguster seules ou a
            partager.
          </h3>
        </div>
      </div>
    ),
    [Category.COCKTAIL]: (
      <div
        className="flex flex-col justify-between h-full"
        style={{ overflow: 'hidden' }}
      >
        <div style={{ flexShrink: 0 }}>
          <h1
            style={{
              fontSize: isMobile ? 'clamp(1rem, 4vw, 1.5rem)' : '12rem',
              lineHeight: isMobile ? '1.1' : '100%',
              margin: 0,
            }}
          >
            COCKTAIL
          </h1>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            paddingTop: isMobile ? '1rem' : '0',
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.9rem, 3.2vw, 1.3rem)' : '2rem',
              margin: '0.5rem 0',
            }}
          >
            Découvrez un cocktail qui vous fera goûter de la vraie Corée. De
            Sans alcool a mocktail, choisissez parmi une variété de cocktails
            pour vous mettre en appétit.
          </h1>
        </div>
      </div>
    ),
    [Category.MATCHA_SMOOTHIE]: (
      <div
        className="flex flex-col justify-between h-full"
        style={{ overflow: 'hidden' }}
      >
        <div style={{ flexShrink: 0 }}>
          <h1
            style={{
              fontSize: isMobile ? 'clamp(1rem, 4vw, 1.5rem)' : '12rem',
              lineHeight: isMobile ? '1.1' : '100%',
              margin: 0,
            }}
          >
            MATCHA & SMOOTHIE
          </h1>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            paddingTop: isMobile ? '1rem' : '0',
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.9rem, 3.2vw, 1.3rem)' : '2rem',
              margin: '0.5rem 0',
            }}
          >
            Nous vous présentons une boisson à la fois familière et inédite.
            Nous proposons une variété de saveurs coréennes, dont un délicieux
            matcha et un riche sésame noir, tous préparés en Corée.
          </h1>
        </div>
      </div>
    ),
    [Category.DESSERT]: (
      <div
        className="flex flex-col justify-between h-full"
        style={{ overflow: 'hidden' }}
      >
        <div style={{ flexShrink: 0 }}>
          <h1
            style={{
              fontSize: isMobile ? 'clamp(1rem, 4vw, 1.5rem)' : '12rem',
              lineHeight: isMobile ? '1.1' : '100%',
              margin: 0,
            }}
          >
            DESSERT
          </h1>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            paddingTop: isMobile ? '1rem' : '0',
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 'clamp(0.9rem, 3.2vw, 1.3rem)' : '2rem',
              margin: '0.5rem 0',
            }}
          >
            Savourez une glace au goût riche, délicieuse après un repas ou tout
            simplement nature.
          </h1>
        </div>
      </div>
    ),
  };

  const categoryVideoMap = {
    [Category.BURGER]: `${cloudfrontUrl}/food/burger.mp4`,
    [Category.CORNDOG]: `${cloudfrontUrl}/food/corndog.mp4`,
    [Category.CHICKEN]: `${cloudfrontUrl}/food/category-chicken.mp4`,
    [Category.SIGATURE_PLATTER]: `${cloudfrontUrl}/anime_avec_une_main_de_pere_noel_qui_prend_une_pomme_de_terre.mp4`,
    [Category.SIDE]: `${cloudfrontUrl}/anime_le_personnage.mp4`,
    [Category.DESSERT]: `${cloudfrontUrl}/food/glace.mp4`,
    [Category.COCKTAIL]: `${cloudfrontUrl}/ksmbeverage.mp4`,
    [Category.MATCHA_SMOOTHIE]: `${cloudfrontUrl}/food/matcha.mp4`,
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Category Navigation - Mobile (Top) */}
      <div className="bg-white py-8 px-8 border-b block md:hidden">
        <div className="flex justify-start overflow-x-auto scrollbar-hide pb-2 space-x-8">
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`relative group transition-all duration-300 flex-shrink-0 ${
                selectedCategory === category.key
                  ? 'text-gray-700'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {/* Circle Icon */}
              <div
                className={`w-16 h-16 rounded-full border flex items-center justify-center mb-2 mx-auto transition-all duration-300 overflow-hidden ${
                  selectedCategory === category.key
                    ? 'border-gray-600 bg-white'
                    : 'border-gray-300 group-hover:border-gray-400 bg-white'
                }`}
              >
                {/* Category Image */}
                <img
                  src={categoryButtonImageMap[category.key]}
                  alt={category.key}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Category Label */}
              <span className="text-sm font-medium whitespace-nowrap">
                {category.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <Box sx={{ width: '100%', height: '100%' }}>
        {isMobile ? (
          // Mobile: Vertical layout - video on top, text below
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
            }}
          >
            {/* Video Section */}
            <Box
              sx={{
                width: '100%',
                height: '50%',
                aspectRatio: '1/1',
                border: `${SECTION_BORDER_PIXEL} solid #000`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <video
                src={categoryVideoMap[selectedCategory]}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>

            {/* Text Section */}
            <Box
              sx={{
                width: '100%',
                minHeight: '50%',
                border: `${SECTION_BORDER_PIXEL} solid #000`,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                backgroundColor: '#f9fafb',
                padding: '0.5rem',
              }}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                {categoryTextMap[selectedCategory]}
              </div>
            </Box>
          </div>
        ) : (
          // Desktop: Side by side layout
          <Grid container sx={{ height: '100%' }}>
            <Grid item xs={0} md={6}>
              <Box
                sx={{
                  height: '100%',
                  aspectRatio: '1/1',
                  border: `${SECTION_BORDER_PIXEL} solid #000`,
                  display: 'flex',
                  alignItems: 'left',
                  justifyContent: 'left',
                  backgroundColor: '#f9fafb',
                  padding: '1rem',
                }}
              >
                {categoryTextMap[selectedCategory]}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: '100%',
                  aspectRatio: '1/1',
                  border: `${SECTION_BORDER_PIXEL} solid #000`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <video
                  src={categoryVideoMap[selectedCategory]}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Category Navigation - Desktop */}
      <div className="bg-white py-8 px-8 lg:px-16 border-b hidden md:block">
        <div className="flex justify-start lg:justify-center lg:space-x-10 overflow-x-auto scrollbar-hide lg:overflow-x-visible pb-2 space-x-8">
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`relative group transition-all duration-300 flex-shrink-0 ${
                selectedCategory === category.key
                  ? 'text-gray-700'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {/* Circle Icon */}
              <div
                className={`w-16 h-16 rounded-full border flex items-center justify-center mb-2 mx-auto transition-all duration-300 overflow-hidden ${
                  selectedCategory === category.key
                    ? 'border-gray-600 bg-white'
                    : 'border-gray-300 group-hover:border-gray-400 bg-white'
                }`}
              >
                {/* Category Image - Empty */}
                <img
                  src={categoryButtonImageMap[category.key]}
                  alt={category.key}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Category Label */}
              <span className="text-sm font-medium whitespace-nowrap">
                {category.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Category Header */}
      <div
        className="py-6 px-8 lg:px-16"
        style={{ backgroundColor: PRIMARY_COLOR }}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">
            {selectedCategory} ({filteredProducts.length})
          </h2>
        </div>
      </div>

      <Box sx={{ width: '100%', height: '100%' }}>
        <Grid container sx={{ height: '100%' }}>
          {filteredProducts.map((product: Product, index: number) => (
            <Grid
              item
              xs={gridSize.xs}
              md={gridSize.md}
              key={`${product.id}-${index}`}
            >
              <Box
                sx={{
                  height: '100%',
                  aspectRatio: {
                    xs: getAspectRatio(filteredProducts.length).xs,
                    md: getAspectRatio(filteredProducts.length).md,
                  },
                  border: `${SECTION_BORDER_PIXEL} solid #000`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f9fafb',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onClick={() => {
                  // Store current category in history state for browser back button
                  window.history.replaceState(
                    { category: selectedCategory },
                    '',
                    window.location.href,
                  );
                  navigate(
                    `/products/${product.id}?category=${selectedCategory}`,
                  );
                }}
              >
                {product.images && product.images[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      height: '70%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    No Image
                  </div>
                )}

                {/* Simple centered product name */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '8px',
                    right: '8px',
                    color: '#000',
                    fontSize: '14px',
                    fontWeight: '300',
                    textAlign: 'center',
                    lineHeight: '1.2',
                    maxHeight: '40px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    textShadow:
                      '1px 1px 2px white, -1px -1px 2px white, 1px -1px 2px white, -1px 1px 2px white',
                  }}
                >
                  {product.name}
                </div>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </div>
  );
}
